// Studio team Inc 2 — per-broadcast agenda CRUD + reorder. All RLS-gated by
// room_agenda_team_all (can_produce_room: host ∪ cohost ∪ approved show member).
// Reorder is an integer-rewrite (agendas are short; position has no unique
// constraint, so transient duplicates during the rewrite are harmless).

import { supabase } from '@/lib/supabase';

export interface AgendaItem {
  id: string;
  roomId: string;
  position: number;
  title: string;
  note: string | null;
  covered: boolean;
  createdBy: string | null;
}

const SELECT = 'id, room_id, position, title, note, covered, created_by';

interface RawAgendaItem {
  id: string;
  room_id: string;
  position: number;
  title: string;
  note: string | null;
  covered: boolean;
  created_by: string | null;
}

function mapItem(r: RawAgendaItem): AgendaItem {
  return {
    id: r.id,
    roomId: r.room_id,
    position: r.position,
    title: r.title,
    note: r.note,
    covered: r.covered,
    createdBy: r.created_by,
  };
}

export async function listAgenda(roomId: string): Promise<AgendaItem[]> {
  const { data, error } = await supabase
    .from('room_agenda_items')
    .select(SELECT)
    .eq('room_id', roomId)
    .order('position', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(mapItem);
}

export async function addAgendaItem(
  roomId: string,
  title: string,
  createdBy: string,
  note?: string,
): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Enter a title');
  const { data: last, error: maxErr } = await supabase
    .from('room_agenda_items')
    .select('position')
    .eq('room_id', roomId)
    .order('position', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (maxErr) throw maxErr;
  const nextPosition = (last?.position ?? 0) + 1;
  const { error } = await supabase.from('room_agenda_items').insert({
    room_id: roomId,
    position: nextPosition,
    title: trimmed,
    note: note?.trim() || null,
    created_by: createdBy,
  });
  if (error) throw error;
}

export async function updateAgendaItem(
  id: string,
  patch: { title?: string; note?: string | null; covered?: boolean },
): Promise<void> {
  const update: { title?: string; note?: string | null; covered?: boolean } = {};
  if (patch.title !== undefined) update.title = patch.title.trim();
  if (patch.note !== undefined) update.note = patch.note?.trim() || null;
  if (patch.covered !== undefined) update.covered = patch.covered;
  const { error } = await supabase.from('room_agenda_items').update(update).eq('id', id);
  if (error) throw error;
}

/** Integer-rewrite the positions to match orderedIds (1-based). */
export async function reorderAgenda(roomId: string, orderedIds: string[]): Promise<void> {
  const results = await Promise.all(
    orderedIds.map((id, i) =>
      supabase
        .from('room_agenda_items')
        .update({ position: i + 1 })
        .eq('id', id)
        .eq('room_id', roomId),
    ),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

export async function deleteAgendaItem(id: string): Promise<void> {
  const { error } = await supabase.from('room_agenda_items').delete().eq('id', id);
  if (error) throw error;
}
